import { useState } from "react";
import { Mail, Phone, MapPin, MessageCircle, Send, Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";

const PHONE_NUMBER = "(242) 805-6073";
const WHATSAPP_LINK = "https://wa.me/12428056073";

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate form submission
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    toast.success("Message sent! We'll get back to you within 24-48 hours.");
    setFormData({ name: "", email: "", subject: "", message: "" });
    setIsSubmitting(false);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero */}
      <section className="py-16 md:py-20" style={{ background: 'linear-gradient(135deg, #f8fffe 0%, #ffffff 100%)' }}>
        <div className="container-main">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6" style={{ color: '#2D3748' }}>
              Contact Us
            </h1>
            <p className="text-lg text-gray-600">
              Have questions? We're here to help. Reach out and we'll respond as soon as possible.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-16">
        <div className="container-main">
          <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-12">
            {/* Contact Info */}
            <div>
              <h2 className="text-2xl font-bold mb-6" style={{ color: '#2D3748' }}>Get in Touch</h2>
              <p className="text-gray-600 mb-8">
                Whether you have a question about our pre-qualification process, need help with 
                your application, or want to provide feedback, we'd love to hear from you.
              </p>

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(82, 191, 184, 0.15)' }}>
                    <Mail className="w-6 h-6" style={{ color: '#52BFB8' }} />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1" style={{ color: '#2D3748' }}>Email</h3>
                    <a 
                      href="mailto:support@financify-caribbean.com" 
                      className="text-gray-600 hover:text-[#52BFB8] transition-colors"
                    >
                      support@financify-caribbean.com
                    </a>
                    <p className="text-sm text-gray-500 mt-1">We respond within 24-48 hours</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(82, 191, 184, 0.15)' }}>
                    <MessageCircle className="w-6 h-6" style={{ color: '#52BFB8' }} />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1" style={{ color: '#2D3748' }}>WhatsApp</h3>
                    <a 
                      href={WHATSAPP_LINK}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gray-600 hover:text-[#52BFB8] transition-colors"
                    >
                      {PHONE_NUMBER}
                    </a>
                    <p className="text-sm text-gray-500 mt-1">Quick questions? Message us!</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(82, 191, 184, 0.15)' }}>
                    <Phone className="w-6 h-6" style={{ color: '#52BFB8' }} />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1" style={{ color: '#2D3748' }}>Phone</h3>
                    <a href="tel:+12428056073" className="text-gray-600 hover:text-[#52BFB8]">{PHONE_NUMBER}</a>
                    <p className="text-sm text-gray-500 mt-1">Mon-Fri, 9am-5pm EST</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(82, 191, 184, 0.15)' }}>
                    <MapPin className="w-6 h-6" style={{ color: '#52BFB8' }} />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1" style={{ color: '#2D3748' }}>Location</h3>
                    <span className="text-gray-600">Nassau, Bahamas</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
              <h2 className="text-2xl font-bold mb-6" style={{ color: '#2D3748' }}>Send a Message</h2>
              
              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <Label htmlFor="name">Your Name</Label>
                  <Input
                    id="name"
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    required
                    placeholder="John Doe"
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="email">Email Address</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    required
                    placeholder="john@example.com"
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="subject">Subject</Label>
                  <Input
                    id="subject"
                    type="text"
                    value={formData.subject}
                    onChange={(e) => setFormData({...formData, subject: e.target.value})}
                    required
                    placeholder="How can we help?"
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="message">Message</Label>
                  <textarea
                    id="message"
                    value={formData.message}
                    onChange={(e) => setFormData({...formData, message: e.target.value})}
                    required
                    placeholder="Your message..."
                    rows={5}
                    className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  />
                </div>

                <Button 
                  type="submit" 
                  className="btn-primary w-full"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      Send Message
                      <Send className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
